// ÁTTEKINTÉS — „Rám vár" kártya: a bejelentkezett felhasználó nyitott teendői, egy kattintással a munkáig.
//
// MIÉRT EZ A LEGFONTOSABB BLOKK: a kezdőlapon nem statisztikát keresünk, hanem azt, hogy „mi az én dolgom
// most". Négy tétel — mind a MEGLÉVŐ lista-végpontból, mind a SAJÁT jogához kötve:
//   • rám osztott, nem lezárt jegyek (`tickets.view`, a lista `mineGroups` számlálója),
//   • válaszra váró megbeszélés-meghívó (`meetings.view`),
//   • döntésre váró eszköz-igény (`asset_requests.decide`),
//   • kiállításra váró számla-piszkozat (`invoices.manage`).
//
// A NULLA TÉTELT NEM MUTATJUK (inbox-zero elv): a kártya csak azt sorolja, amivel tenni kell valamit; ha
// minden nulla, egy nyugtázó üres-állapot jön. Így a kártya magassága a tényleges teendőt tükrözi.
//
// FIX, teljes szélességű sáv: a sorok desktopon egymás mellett (2 / 4 hasáb), mobilon egymás alatt.
//
// A SORBAN NINCS NYÍL (user-döntés): a sor egésze link, az affordanciát a hover-háttér adja — a
// sor-végi nyilak négy sorban négyszer ismételték ugyanazt a jelzést, és zajt vittek a számok mellé.
import {
  BanknotesIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  TicketIcon,
} from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import { type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { CardRow, CardRows } from '@/components/shared/card-rows';
import { PanelCard, PanelCardEmpty } from '@/components/shared/panel-card';
import type { WorkItem } from '@/features/dashboard/use-dashboard-data';

/** Tétel-kulcs → ikon (a lista-oldalak ikon-nyelvét követi: jegy · naptár · eszköz · pénz). */
const ICONS: Record<WorkItem['key'], ComponentType<SVGProps<SVGSVGElement>>> = {
  myTickets: TicketIcon,
  rsvp: CalendarDaysIcon,
  assetRequests: ComputerDesktopIcon,
  draftInvoices: BanknotesIcon,
};

/**
 * Az oszlopszám a TÉTELEK számához igazodik (statikus osztály-tábla, hogy a Tailwind lássa): egy tételnél a fix
 * négy oszlop a szöveget levágta („Kiállításra váró piszko…"), a sor háromnegyede pedig üres maradt.
 */
const WORK_GRID_COLS: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-2 xl:grid-cols-3',
  4: 'md:grid-cols-2 xl:grid-cols-4',
};

export interface MyWorkCardProps {
  items: WorkItem[];
  loading: boolean;
}

export function MyWorkCard({ items, loading }: MyWorkCardProps): JSX.Element {
  const { t } = useTranslation();
  const pending = items.filter((i) => i.count > 0);

  return (
    <PanelCard title={t('dashboard.myWork.title')} loading={loading}>
      {pending.length === 0 ? (
        <PanelCardEmpty>
          <span className="flex items-center gap-2">
            <CheckCircleIcon className="text-success size-5 shrink-0" aria-hidden />
            {t('dashboard.myWork.empty')}
          </span>
        </PanelCardEmpty>
      ) : (
        <CardRows
          className={`md:grid md:gap-1 md:p-2 md:[&>li]:after:hidden ${WORK_GRID_COLS[Math.min(pending.length, 4)] ?? WORK_GRID_COLS[4]}`}
        >
          {pending.map((item) => {
            const Icon = ICONS[item.key];
            return (
              <CardRow key={item.key}>
                <Link
                  // A cél MÁR SZŰRT lista (pl. „csak az enyém" jegyek) — a kezdőlapról egy kattintás a
                  // munkáig. A típusos `to`/`search` itt adat-vezérelt, ezért cast (a literálok a hookban).
                  to={item.to as never}
                  search={item.search as never}
                  className="hover:bg-accent/60 focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset md:rounded-lg"
                >
                  <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden />
                  <span className="text-md text-foreground min-w-0 flex-1 truncate">
                    {t(`dashboard.myWork.items.${item.key}`)}
                  </span>
                  <span className="text-md text-foreground font-semibold tabular-nums">
                    {item.count}
                  </span>
                </Link>
              </CardRow>
            );
          })}
        </CardRows>
      )}
    </PanelCard>
  );
}
